import React, { useState } from 'react';
import { Card, Typography, Box, IconButton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { urlFor } from '../configs/sanityClient';

const constantImageUrl = 'https://static.vecteezy.com/system/resources/thumbnails/035/147/887/small/ai-generated-antique-armchair-throne-on-transparent-background-image-png.png';

const ObjetCard = ({ objet }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleCardClick = () => {
    navigate(`/details/${objet.id || objet._id}`);
  };

  const getImageUrl = (image) => {
    try {
      const assetRef = image.file?.asset?._ref;
      if (assetRef) {
        return urlFor(image.file).url();
      }
    } catch (error) {
      console.error('Error building Sanity image URL:', error);
    }
    return image?.path || constantImageUrl;
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => {
      const images = objet.images?.filter(img => img !== null && img !== undefined) || [];
      return (prevIndex + 1) % Math.max(images.length, 1);
    });
  };

  const handleBack = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prevIndex) => {
      const images = objet.images?.filter(img => img !== null && img !== undefined) || [];
      return (prevIndex - 1 + images.length) % Math.max(images.length, 1);
    });
  };

  const renderImages = () => {
    if (!objet.images || !Array.isArray(objet.images) || objet.images.length === 0) {
      return (
        <div key={`${objet._id}-default`}>
          <img
            src={constantImageUrl}
            alt="Default Image"
            style={{
              width: "100%",
              height: "200px",
              objectFit: "contain",
              borderTopLeftRadius: "4px",
              borderTopRightRadius: "4px",
              margin: "0",
              display: "block",
            }}
          />
        </div>
      );
    }

    const filteredImages = objet.images.filter(image => image !== null && image !== undefined);
    const currentImage = filteredImages[currentImageIndex];

    return (
      <Box sx={{ position: 'relative' }}>
        <img
          src={getImageUrl(currentImage)}
          alt={`${objet.nom} - Image ${currentImageIndex + 1}`}
          style={{
            width: "100%",
            height: "200px",
            objectFit: "contain",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
            margin: "0",
            display: "block",
          }}
          onError={(e) => {
            e.target.src = constantImageUrl;
            e.target.alt = `${objet.nom} - Fallback Image`;
          }}
        />
        {filteredImages.length > 1 && (
          <>
            <IconButton
              onClick={handleBack}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
                zIndex: 1,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                },
                zIndex: 1,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 0.5,
                zIndex: 1,
              }}
            >
              {filteredImages.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: currentImageIndex === index ? '#A68E5D' : 'rgba(255, 255, 255, 0.5)',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        backgroundColor: "#232323",
        color: "#A68E5D",
        textAlign: "center",
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: 'pointer',
        maxWidth: 200,
        margin: '0 auto',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.3)',
        }
      }}
    >
      {renderImages()}

      <Box
        sx={{
          backgroundColor: "#232323",
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px",
          textAlign: "left",
          padding: '10px',
        }}
      >
        <Typography variant="subtitle1" sx={{ color: "#FFFFFF" }}>
          {objet?.nom}
        </Typography>
        <Typography variant="caption" sx={{ color: "grey" }}>
          {objet?.categorie?.nom}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
          <Typography variant="subtitle2" sx={{ color: "grey", display: "flex", alignItems: "center" }}>
            <LocationOnIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            {objet?.pays?.nom || 'Unknown Location'}
          </Typography>
          <Typography sx={{ color: "white", fontWeight: "bold" }}>
            {objet?.prix}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ObjetCard;
